import React, { useState } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  Typography,
  MenuItem,
} from '@mui/material';
import { Add, Edit, Delete, LocationCity } from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/api';

const CityPricingTab = ({ data, onRefresh, onSuccess, onError }) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    cityName: '',
    country: 'Saudi Arabia',
    basePrice: 0,
    seasonalMultiplier: 1.0,
    demandLevel: 'Medium',
    importanceScore: 50,
    isActive: true,
    displayOrder: 1,
  });

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        cityName: item.cityName,
        country: item.country,
        basePrice: item.basePrice,
        seasonalMultiplier: item.seasonalMultiplier,
        demandLevel: item.demandLevel,
        importanceScore: item.importanceScore,
        isActive: item.isActive,
        displayOrder: item.displayOrder,
      });
    } else {
      setEditingItem(null);
      setFormData({
        cityName: '',
        country: 'Saudi Arabia',
        basePrice: 0,
        seasonalMultiplier: 1.0,
        demandLevel: 'Medium',
        importanceScore: 50,
        isActive: true,
        displayOrder: data.length + 1,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingItem(null);
  };

  const handleSave = async () => {
    try {
      if (editingItem) {
        await axios.put(
          `${API_BASE_URL}/pricing/cities/${editingItem.id}`,
          formData
        );
        onSuccess('City pricing updated successfully');
      } else {
        await axios.post(`${API_BASE_URL}/pricing/cities`, formData);
        onSuccess('City pricing created successfully');
      }
      handleCloseDialog();
      onRefresh();
    } catch (err) {
      onError('Failed to save: ' + err.message);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this city pricing?')) {
      try {
        await axios.delete(`${API_BASE_URL}/pricing/cities/${id}`);
        onSuccess('City pricing deleted successfully');
        onRefresh();
      } catch (err) {
        onError('Failed to delete: ' + err.message);
      }
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h6">City Pricing Configuration</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Add City Pricing
        </Button>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>City</TableCell>
              <TableCell>Country</TableCell>
              <TableCell>Base Price</TableCell>
              <TableCell>Seasonal Multiplier</TableCell>
              <TableCell>Demand Level</TableCell>
              <TableCell>Importance</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.sort((a, b) => a.displayOrder - b.displayOrder).map((item) => (
              <TableRow key={item.id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationCity fontSize="small" color="primary" />
                    <Typography fontWeight="bold">{item.cityName}</Typography>
                  </Box>
                </TableCell>
                <TableCell>{item.country}</TableCell>
                <TableCell>
                  <Typography fontWeight="bold" color="primary">
                    ${item.basePrice.toFixed(2)}
                  </Typography>
                </TableCell>
                <TableCell>{item.seasonalMultiplier.toFixed(2)}×</TableCell>
                <TableCell>
                  <Chip
                    label={item.demandLevel}
                    color={item.demandLevel === 'High' ? 'error' : item.demandLevel === 'Medium' ? 'warning' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>{item.importanceScore}/100</TableCell>
                <TableCell>
                  <Chip
                    label={item.isActive ? 'Active' : 'Inactive'}
                    color={item.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleOpenDialog(item)}
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDelete(item.id)}
                  >
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingItem ? 'Edit City Pricing' : 'Add City Pricing'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="City Name"
              value={formData.cityName}
              onChange={(e) =>
                setFormData({ ...formData, cityName: e.target.value })
              }
              fullWidth
            />
            <TextField
              label="Country"
              value={formData.country}
              onChange={(e) =>
                setFormData({ ...formData, country: e.target.value })
              }
              fullWidth
            />
            <TextField
              label="Base Price ($)"
              type="number"
              value={formData.basePrice}
              onChange={(e) =>
                setFormData({ ...formData, basePrice: parseFloat(e.target.value) })
              }
              inputProps={{ min: 0, step: 0.01 }}
              fullWidth
            />
            <TextField
              label="Seasonal Multiplier"
              type="number"
              value={formData.seasonalMultiplier}
              onChange={(e) =>
                setFormData({ ...formData, seasonalMultiplier: parseFloat(e.target.value) })
              }
              inputProps={{ min: 0, step: 0.01 }}
              fullWidth
            />
            <TextField
              select
              label="Demand Level"
              value={formData.demandLevel}
              onChange={(e) =>
                setFormData({ ...formData, demandLevel: e.target.value })
              }
              fullWidth
            >
              <MenuItem value="Low">Low</MenuItem>
              <MenuItem value="Medium">Medium</MenuItem>
              <MenuItem value="High">High</MenuItem>
            </TextField>
            <TextField
              label="Importance Score (0-100)"
              type="number"
              value={formData.importanceScore}
              onChange={(e) =>
                setFormData({ ...formData, importanceScore: parseInt(e.target.value) })
              }
              inputProps={{ min: 0, max: 100 }}
              fullWidth
            />
            <TextField
              label="Display Order"
              type="number"
              value={formData.displayOrder}
              onChange={(e) =>
                setFormData({ ...formData, displayOrder: parseInt(e.target.value) })
              }
              inputProps={{ min: 1 }}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) =>
                    setFormData({ ...formData, isActive: e.target.checked })
                  }
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CityPricingTab;
